import { useState } from "react";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AnalyticsAndReportsNavigation,
  SettingsNavigation,
  SidebarNavigation,
} from "./index";
import {
  adminLogo,
  queueAdminLogo,
  mailIcon,
  webIcon,
  PhoneIcon,
} from "../../../assets/icons/index";
import MenuButton from "../MenuButton/";
import classes from "./SideBar.module.scss";
import React from "react";
import { mail, webSite } from "../../../constants/data";
import { adminRoutesURLs } from "../../../constants/routes";
import { getUserData } from "../../../utils/Authorization/authorization";
import { useMyPermissions } from "../../../hooks/useMyPermissions";

const SideBar = () => {
  const [activeDropDownItem, setDropDownItem] = useState(
    AnalyticsAndReportsNavigation[0].name,
  );
  const [activeDropDownItemURL, setDropDownItemURL] = useState(
    adminRoutesURLs.analyticsAndReports.currentServicesStatus,
  );
  const [activeSettingsItem, setActiveSettingsItem] = useState(
    SettingsNavigation[0].name,
  );
  const [activeSettingsItemURL, setActiveSettingsItemURL] = useState(
    SettingsNavigation[0].url,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const activeURL = useMemo<string>(() => {
    return location.pathname;
  }, [location]);

  React.useEffect(() => {
    if (
      !activeURL.includes(adminRoutesURLs.terminalAppearance) &&
      !activeURL.includes(adminRoutesURLs.tabloSettings) &&
      !activeURL.includes(adminRoutesURLs.ratingSettings)
    ) {
      return;
    }

    const matchedSettingsItem = SettingsNavigation.find((item) =>
      activeURL.includes(item.url),
    );

    if (matchedSettingsItem) {
      setActiveSettingsItem(matchedSettingsItem.name);
      setActiveSettingsItemURL(matchedSettingsItem.url);
    }
  }, [activeURL]);

  const { canView, canEdit } = useMyPermissions();

  const userRole = ((getUserData() as { role?: string })?.role ?? "").trim();
  const isSuperAdmin = userRole === "Суперадминистратор Tehnosky";
  const isBranchAdmin = userRole.startsWith("Администратор");
  const isAdmin = isBranchAdmin;

  const sidebarPermissionKeyByName: Record<string, string> = {
    Филиалы: "", // показываем по роли, не по permission
    "Категории и услуги": "SECTION_SERVICES",
    "Рабочие места": "WORKPLACES",
    Сотрудники: "EMPLOYERS",
    Аудит: "", // только по роли суперадмина
    "Аналитика и отчеты": "ANALYTICS_GROUP",
    Настройки: "SETTINGS_GROUP",
    "Личный кабинет": "USER_AREA",
  };

  const analyticsPermissionKeyByName: Record<string, string> = {
    "Текущее состояние обслуживания посетителей":
      "ANALYTICS_CURRENT_SERVICES_STATUS",
    "По услугам": "ANALYTICS_SERVICES",
    "По сотрудникам": "ANALYTICS_EMPLOYERS_REPORTS",
    "По посетителям": "ANALYTICS_VISITORS",
    "Оценка качества обслуживания": "ANALYTICS_QUALITY_RATING",
  };

  const settingsPermissionKeyByName: Record<string, string> = {
    Терминал: "SETTINGS_TERMINAL",
    Табло: "SETTINGS_TABLO",
    "Оценка качества": "SETTINGS_RATING",
  };

  type HandleButtonContentType = {
    name: string;
    url: string;
  };
  const handleButtonClick = (content: HandleButtonContentType) => {
    redirectToPostPage(content.url);
  };
  const handleDropDownItem = (content: string): void => {
    setDropDownItem(content);
  };
  const handleSettingsDropDownItem = (content: string): void => {
    setActiveSettingsItem(content);
  };

  const redirectToPostPage = (section: string) => {
    navigate(section);
  };

  const canEditAnySettings =
    canEdit("SETTINGS_TERMINAL") || canEdit("SETTINGS_TABLO") || canEdit("SETTINGS_RATING");
  const canViewAnySettings =
    canView("SETTINGS_TERMINAL") || canView("SETTINGS_TABLO") || canView("SETTINGS_RATING");

  return (
    <div className={classes.sidebar}>
      <div className={classes.companyLogo}>
        <img src={adminLogo} alt="tehnosky-logo" />
      </div>
      <div className={classes.sidebarHeader}>
        <img src={queueAdminLogo} alt="system-queue-logo" />
        <div className={classes.sidebarHeaderText}>
          <p>система управления очередью</p>
        </div>
      </div>
      <ul className={classes.sidebarMenu}>
        {SidebarNavigation.filter((item) => {
          if (item.name === "Филиалы") return isSuperAdmin || isBranchAdmin;
          if (item.name === "Аудит") return isSuperAdmin;
          const key = sidebarPermissionKeyByName[item.name];
          return key ? canView(key) : true;
        }).map((item, index) => (
          <div key={index}>
            {item.name !== "Аналитика и отчеты" && item.name !== "Настройки" ? (
              <li>
                <MenuButton
                  content={item.name}
                  isActive={activeURL.includes(item.url)}
                  onClick={() => {
                    handleButtonClick(item);
                  }}
                />
              </li>
            ) : item.name === "Аналитика и отчеты" ? (
              <li id={classes.liAnalytics}>
                <div className={classes.btnAnalyticsCont}>
                  <MenuButton
                    content="Аналитика и отчеты"
                    isActive={
                      activeURL.includes(item.url) ||
                      activeURL.includes(activeDropDownItemURL)
                    }
                    onClick={() => {
                      handleButtonClick(item);
                      setDropDownItem(AnalyticsAndReportsNavigation[0].name);
                    }}
                  />
                </div>
                {activeURL.includes(
                  adminRoutesURLs.analyticsAndReports.analyticsAndReportsBase,
                ) && (
                  <ul className={classes.analyticsMenu}>
                    {AnalyticsAndReportsNavigation.filter((a) => {
                      const key = analyticsPermissionKeyByName[a.name];
                      return key ? canView(key) : true;
                    }).map((item, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          handleDropDownItem(item.name);
                          setDropDownItemURL(item.url);
                          redirectToPostPage(
                            `${adminRoutesURLs.analyticsAndReports.analyticsAndReportsBase}/${item.url}`,
                          );
                        }}
                      >
                        <div
                          className={classes.item}
                          id={
                            activeDropDownItem === item.name
                              ? classes.active
                              : ""
                          }
                        >
                          {item.name}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li id={classes.liAnalytics}>
                <div className={classes.btnAnalyticsCont}>
                  <MenuButton
                    content="Настройки"
                    isActive={
                      activeURL.includes(adminRoutesURLs.terminalAppearance) ||
                      activeURL.includes(adminRoutesURLs.tabloSettings) ||
                      activeURL.includes(adminRoutesURLs.ratingSettings) ||
                      activeURL.includes(activeSettingsItemURL)
                    }
                    isDisabled={!canViewAnySettings}
                    onClick={() => {
                      if (!canViewAnySettings) return;
                      handleButtonClick(item);
                      setActiveSettingsItem(SettingsNavigation[0].name);
                      setActiveSettingsItemURL(SettingsNavigation[0].url);
                    }}
                  />
                </div>
                {(activeURL.includes(adminRoutesURLs.terminalAppearance) ||
                  activeURL.includes(adminRoutesURLs.tabloSettings) ||
                  activeURL.includes(adminRoutesURLs.ratingSettings)) && (
                  <ul className={classes.analyticsMenu}>
                    {SettingsNavigation.filter((s) => {
                      const key = settingsPermissionKeyByName[s.name];
                      return key ? canView(key) : true;
                    }).map((settingsItem, index) => (
                      <li
                        key={index}
                        className={(() => {
                          const key = settingsPermissionKeyByName[settingsItem.name];
                          if (!key) return undefined;
                          if (!canEdit(key)) return classes.readOnly;
                          return undefined;
                        })()}
                        onClick={() => {
                          const key =
                            settingsPermissionKeyByName[settingsItem.name];
                          if (key && !canView(key)) return;
                          handleSettingsDropDownItem(settingsItem.name);
                          setActiveSettingsItemURL(settingsItem.url);
                          redirectToPostPage(settingsItem.url);
                        }}
                      >
                        <div
                          className={classes.item}
                          id={
                            activeSettingsItem === settingsItem.name
                              ? classes.active
                              : ""
                          }
                        >
                          {settingsItem.name}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </div>
        ))}
      </ul>
      <div className={classes.sidebarFooter}>
        <div className={classes.menuFooterMail}>
          <PhoneIcon />
          <a href={`tel: +375291980004`}>
            <span>+375(29) 198 00 04</span>
          </a>
        </div>

        <div className={classes.menuFooterMail}>
          <img src={mailIcon} alt={"mail"} />
          <a href={`mailto:${mail}`}>
            <span>{mail}</span>
          </a>
        </div>

        <div className={classes.menuFooterLink}>
          <img src={webIcon as unknown as string} alt={"web"} />
          <a href="https://high-tech.by/">
            <span>{webSite}</span>
          </a>
        </div>
      </div>
    </div>
  );
};
export default SideBar;
